// HR Module Constants

// Field labels for forms
export const FIELD_LABELS = {
  // Basic Info
  employeeId: 'Employee ID',
  'personalInfo.dateOfBirth': 'Date of Birth',
  'personalInfo.gender': 'Gender',
  'personalInfo.maritalStatus': 'Marital Status',
  'personalInfo.nationality': 'Nationality',
  'personalInfo.nationalId': 'National ID',
  'personalInfo.bloodGroup': 'Blood Group',
  
  // Contact
  'contact.personalEmail': 'Personal Email',
  'contact.workEmail': 'Work Email',
  'contact.personalPhone': 'Personal Phone',
  'contact.workPhone': 'Work Phone',
  
  // Employment
  'employment.joinDate': 'Join Date',
  'employment.department': 'Department',
  'employment.position': 'Position',
  'employment.level': 'Level',
  'employment.employmentType': 'Employment Type',
  'employment.workLocation': 'Work Location',
  'employment.manager': 'Reporting Manager',
  'employment.status': 'Status',
  
  // Compensation
  'compensation.salary.amount': 'Salary Amount',
  'compensation.salary.currency': 'Currency',
  'compensation.salary.frequency': 'Payment Frequency',
  
  // Leave
  leaveType: 'Leave Type',
  startDate: 'Start Date',
  endDate: 'End Date',
  reason: 'Reason',
  numberOfDays: 'Number of Days',
  
  // Performance
  reviewPeriod: 'Review Period',
  goals: 'Goals',
  rating: 'Rating',
  feedback: 'Feedback'
};

// Option labels for select fields
export const OPTION_LABELS = {
  gender: {
    male: 'Male',
    female: 'Female',
    other: 'Other',
    prefer_not_to_say: 'Prefer not to say'
  },
  
  maritalStatus: {
    single: 'Single',
    married: 'Married',
    divorced: 'Divorced',
    widowed: 'Widowed',
    other: 'Other'
  },
  
  employmentType: {
    full_time: 'Full Time',
    part_time: 'Part Time',
    contract: 'Contract',
    intern: 'Intern',
    consultant: 'Consultant'
  },
  
  employmentStatus: {
    active: 'Active',
    on_leave: 'On Leave',
    terminated: 'Terminated',
    resigned: 'Resigned',
    retired: 'Retired'
  },
  
  workLocation: {
    office: 'Office',
    remote: 'Remote',
    hybrid: 'Hybrid'
  },
  
  employeeLevel: {
    intern: 'Intern',
    junior: 'Junior',
    mid: 'Mid-Level',
    senior: 'Senior',
    lead: 'Lead',
    manager: 'Manager',
    director: 'Director',
    executive: 'Executive'
  },
  
  leaveType: {
    annual: 'Annual Leave',
    sick: 'Sick Leave',
    personal: 'Personal Leave',
    maternity: 'Maternity Leave',
    paternity: 'Paternity Leave',
    bereavement: 'Bereavement Leave',
    unpaid: 'Unpaid Leave',
    compensatory: 'Compensatory Leave',
    work_from_home: 'Work From Home'
  },
  
  leaveStatus: {
    draft: 'Draft',
    pending: 'Pending Approval',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    withdrawn: 'Withdrawn'
  },
  
  performanceRating: {
    1: 'Needs Improvement',
    2: 'Below Expectations',
    3: 'Meets Expectations',
    4: 'Exceeds Expectations',
    5: 'Outstanding'
  },
  
  reviewType: {
    annual: 'Annual Review',
    semi_annual: 'Semi-Annual Review',
    quarterly: 'Quarterly Review',
    probation: 'Probation Review',
    ad_hoc: 'Ad-hoc Review'
  }
};

// Department options
export const DEPARTMENTS = [
  'Engineering',
  'Marketing',
  'Sales',
  'Finance',
  'Human Resources',
  'Operations',
  'Product',
  'Design',
  'Customer Service',
  'Legal',
  'Research & Development',
  'Quality Assurance',
  'Other'
];

// Currency options
// Static currencies for fallback (when dynamic fetch fails)
export const CURRENCIES = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EGP', label: 'EGP - Egyptian Pound' },
  { value: 'SAR', label: 'SAR - Saudi Riyal' },
  { value: 'QAR', label: 'QAR - Qatari Riyal' },
  { value: 'AED', label: 'AED - UAE Dirham' },
  { value: 'KWD', label: 'KWD - Kuwaiti Dinar' },
  { value: 'BHD', label: 'BHD - Bahraini Dinar' },
  { value: 'OMR', label: 'OMR - Omani Rial' }
];

// Function to get currencies (with dynamic loading)
export const getCurrencies = async () => {
  try {
    const { getDynamicCurrencyOptions } = await import('../utils/dynamicCurrencyUtils');
    return await getDynamicCurrencyOptions();
  } catch (error) {
    console.error('Failed to load dynamic currencies:', error);
    return [];
  }
};

// Validation rules
export const VALIDATION_RULES = {
  employee: {
    userId: { required: true },
    'employment.joinDate': { required: true },
    'employment.department': { required: true },
    'employment.position': { required: true },
    'employment.employmentType': { required: true }
  },
  
  leave: {
    type: { required: true },
    startDate: { required: true },
    endDate: { required: true },
    reason: { required: true, minLength: 10 }
  },
  
  performance: {
    employee: { required: true },
    reviewer: { required: true },
    'reviewPeriod.startDate': { required: true },
    'reviewPeriod.endDate': { required: true },
    reviewType: { required: true }
  }
};

// Default values for forms
export const DEFAULT_VALUES = {
  employee: {
    personalInfo: {
      gender: '',
      maritalStatus: '',
      nationality: '',
      bloodGroup: ''
    },
    contact: {
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      },
      emergencyContacts: []
    },
    employment: {
      employmentType: 'full_time',
      workLocation: 'office',
      status: 'active',
      level: 'junior'
    },
    compensation: {
      salary: {
        currency: 'USD',
        frequency: 'monthly'
      },
      benefits: []
    },
    leaveBalance: {
      annual: { entitled: 21, used: 0, remaining: 21 },
      sick: { entitled: 10, used: 0, remaining: 10 },
      personal: { entitled: 5, used: 0, remaining: 5 }
    }
  },
  
  leave: {
    type: 'annual',
    isHalfDay: false,
    status: 'draft',
    documents: []
  },
  
  performance: {
    reviewType: 'annual',
    status: 'draft',
    goals: [],
    competencyRatings: [],
    feedback360: []
  }
};